import React, { useEffect, useMemo, useState } from 'react';
import {
  getIslandAreaMapPoints,
  getIslandCapabilities,
  getIslandFromHostname,
  getIslandRegionsGuidePlanningPostSlugs,
} from 'alakai-aloha-shared';
import { RegionsGuideView } from 'alakai-aloha-shared/public-ui';
import { useRegionsData } from '../../hooks/useRegionsData';
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { trackEvent } from '../../utils/analytics';

function mapBlogPost(post) {
  return {
    id: post._id,
    title: post.title,
    slug: post.slug?.current || post.slug,
    excerpt: post.excerpt,
    imageUrl: post.mainImage?.asset?.url,
    imageAlt: post.mainImage?.alt || post.title,
  };
}

const RegionsGuide = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const siteContext = useMemo(() => getRuntimeSiteContext(), []);
  const runtimeIsland = useMemo(() => {
    if (typeof window !== 'undefined') {
      return getIslandFromHostname(window.location.hostname) || siteContext.island;
    }

    return siteContext.island;
  }, [siteContext.island]);
  const capabilities = useMemo(() => getIslandCapabilities(runtimeIsland), [runtimeIsland]);
  const areaMapPoints = useMemo(() => getIslandAreaMapPoints(runtimeIsland), [runtimeIsland]);
  const planningPostSlugs = useMemo(() => getIslandRegionsGuidePlanningPostSlugs(runtimeIsland), [runtimeIsland]);
  const { regionsData, regionBlurb, areaBlurb, loading } = useRegionsData();

  useEffect(() => {
    const fetchBlogPosts = async () => {
      if (planningPostSlugs.length === 0) {
        setBlogPosts([]);
        return;
      }

      const blogQuery = `*[_type == "blogPost" && ${islandScopedDocumentFilter()} && slug.current in ${JSON.stringify(planningPostSlugs)}] {
        _id,
        title,
        slug,
        excerpt,
        mainImage {
          asset->{ url },
          alt
        }
      } | order(slug.current)`;

      try {
        const blogData = await sanityFetch(blogQuery);
        setBlogPosts(blogData.map(mapBlogPost));
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      }
    };

    fetchBlogPosts();
  }, [planningPostSlugs]);

  if (loading) {
    return (
      <div className="relative z-20 min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading regions guide...</div>
      </div>
    );
  }

  return (
    <RegionsGuideView
      islandKey={runtimeIsland}
      regions={(regionsData || []).map((region) => ({
        id: region._id,
        title: region.title,
        slug: region.slug?.current || region.slug,
        oneLiner: region.oneLiner,
        shortDescription: region.shortDescription,
        heroImageUrl: region.heroImage?.asset?.url,
        heroImageAlt: region.heroImage?.alt,
        areaCount: region.areaCount,
      }))}
      regionBlurb={regionBlurb}
      areaBlurb={areaBlurb}
      planningPosts={blogPosts}
      areaMapPoints={areaMapPoints}
      mapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
      showCatalogLinks={capabilities.profileCatalogs}
      showPlanningSection={blogPosts.length > 0 || capabilities.stayQuiz || capabilities.tripPlanner}
      onQuizClick={() => {
        trackEvent('quiz_cta_click', { source: 'regions_planning_section', cta: 'primary' });
      }}
      onInterviewClick={() => {
        trackEvent('interview_cta_click', { source: 'regions_planning_section', cta: 'secondary' });
      }}
    />
  );
};

export default RegionsGuide;
